import type { Auction } from '@/types/auctions';
import { Loader } from '@/components/common/Loader';
import { Button } from '@/components/common/Button';
import { fromMicroUnits } from '@/services/auction-messages';
import { text } from '@/config/theme';

interface AuctionListProps {
  auctions: Auction[];
  onBid: (auction: Auction) => void;
  isLoading: boolean;
}

export function AuctionList({ auctions, onBid, isLoading }: AuctionListProps): JSX.Element {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader size="lg" />
      </div>
    );
  }

  if (auctions.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className={`mx-auto h-12 w-12 ${text.tertiary}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
        <h3 className={`mt-2 text-sm font-medium ${text.primary}`}>No active auctions</h3>
        <p className={`mt-1 text-sm ${text.tertiary}`}>
          There are currently no active auctions. Check back soon!
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className={`px-6 py-3 text-left text-xs font-medium ${text.tertiary} uppercase tracking-wider`}
            >
              Auction ID
            </th>
            <th
              scope="col"
              className={`px-6 py-3 text-left text-xs font-medium ${text.tertiary} uppercase tracking-wider`}
            >
              Token Amount
            </th>
            <th
              scope="col"
              className={`px-6 py-3 text-right text-xs font-medium ${text.tertiary} uppercase tracking-wider group relative`}
            >
              <span className="flex items-center justify-end gap-1 cursor-help">
                Est Value
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </span>
              <div className="absolute right-0 top-full mt-1 hidden group-hover:block z-50 w-64 p-2 text-xs normal-case tracking-normal font-normal text-white bg-gray-800 rounded shadow-lg">
                Prices are based on Uniswap and may be inaccurate
              </div>
            </th>
            <th
              scope="col"
              className={`px-6 py-3 text-left text-xs font-medium ${text.tertiary} uppercase tracking-wider`}
            >
              Highest Bid
            </th>
            <th
              scope="col"
              className={`px-6 py-3 text-left text-xs font-medium ${text.tertiary} uppercase tracking-wider`}
            >
              Bidder
            </th>
            <th scope="col" className={`px-6 py-3 text-right text-xs font-medium ${text.tertiary} uppercase tracking-wider`}>
              Action
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {auctions.map((auction) => {
            // Use display info from backend if available, otherwise fall back to manual parsing
            const tokenAmount = auction.display != null
              ? `${auction.display.amount_display} ${auction.display.token_symbol}`
              : auction.amount != null
                ? `${fromMicroUnits(auction.amount.amount)} ${getDenomSymbol(auction.amount.denom)}`
                : 'N/A';
            
            const highestBid = auction.display?.highest_bid_display 
              ?? (auction.highest_bid != null
                ? `${fromMicroUnits(auction.highest_bid.bid_amount)} GRAV`
                : 'No bids');
            
            const usdValue = auction.display?.usd_value_display ?? null;
            
            const bidder = auction.highest_bid?.bidder_address ?? 'None';

            return (
              <tr key={auction.id} className="hover:bg-gray-50">
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${text.primary}`}>
                  #{auction.id}
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${text.primary}`}>
                  {tokenAmount}
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${usdValue != null ? 'text-green-600 font-medium' : text.tertiary}`}>
                  {usdValue ?? '—'}
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${text.primary}`}>
                  {highestBid}
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${text.tertiary}`}>
                  {bidder !== 'None' ? truncateAddress(bidder) : bidder}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <Button
                    onClick={() => { onBid(auction); }}
                    size="sm"
                    variant="primary"
                  >
                    Place Bid
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Get denomination symbol from denom string (fallback for missing display info)
 */
function getDenomSymbol(denom: string): string {
  if (denom.startsWith('gravity0x')) {
    // ERC20 token - extract and display contract address
    return denom.substring(7, 15) + '...';
  }
  if (denom === 'ugraviton') {
    return 'GRAV';
  }
  return denom.toUpperCase();
}

/**
 * Truncate address to show first and last 6 characters
 */
function truncateAddress(address: string): string {
  if (address.length <= 13) {
    return address;
  }
  return `${address.slice(0, 6)}...${address.slice(-6)}`;
}
